import { useRef, useState } from "react";
import api from "@/lib/api";
import Box from "@mui/material/Box";
import Typography from "@mui/material/Typography";
import IconButton from "@mui/material/IconButton";
import CircularProgress from "@mui/material/CircularProgress";
import { alpha } from "@mui/material/styles";
import { UploadCloud, X, Star } from "lucide-react";
import { toast } from "sonner";

const MAX_IMAGES = 12;
const MAX_SIZE = 8 * 1024 * 1024;
const ALLOWED = ["image/jpeg", "image/png", "image/webp", "image/gif"];

export default function PhotoUploader({ value = [], onChange, testIdPrefix = "villa-photo" }) {
  const [dragging, setDragging] = useState(false);
  const [uploading, setUploading] = useState(false);
  const inputRef = useRef(null);

  const uploadFiles = async (fileList) => {
    const files = Array.from(fileList);
    if (!files.length) return;
    if (value.length + files.length > MAX_IMAGES) {
      toast(`You can upload up to ${MAX_IMAGES} photos`);
      return;
    }
    const rejected = files.find((f) => !ALLOWED.includes(f.type) || f.size > MAX_SIZE);
    if (rejected) {
      toast(!ALLOWED.includes(rejected.type) ? "Only JPEG, PNG, WEBP or GIF images are allowed" : "Each photo must be under 8MB");
      return;
    }

    const formData = new FormData();
    files.forEach((f) => formData.append("images", f));

    setUploading(true);
    try {
      const { data } = await api.post("/uploads/villa-images", formData);
      onChange([...value, ...data.urls]);
      toast(`${data.urls.length} photo${data.urls.length > 1 ? "s" : ""} uploaded`);
    } catch (err) {
      toast(err.response?.data?.detail || "Could not upload photos");
    } finally {
      setUploading(false);
    }
  };

  const remove = (url) => onChange(value.filter((u) => u !== url));
  const makeCover = (url) => onChange([url, ...value.filter((u) => u !== url)]);

  return (
    <Box>
      <Box
        onClick={() => inputRef.current?.click()}
        onDragOver={(e) => { e.preventDefault(); setDragging(true); }}
        onDragLeave={() => setDragging(false)}
        onDrop={(e) => { e.preventDefault(); setDragging(false); uploadFiles(e.dataTransfer.files); }}
        data-testid={`${testIdPrefix}-dropzone`}
        sx={{
          border: "2px dashed", borderColor: dragging ? "primary.main" : "divider",
          borderRadius: 4, py: 5, textAlign: "center", cursor: "pointer",
          bgcolor: dragging ? (t) => alpha(t.palette.primary.main, 0.06) : "transparent",
          transition: "all 0.15s ease",
        }}
      >
        <input
          ref={inputRef}
          type="file"
          accept="image/jpeg,image/png,image/webp,image/gif"
          multiple
          hidden
          onChange={(e) => { uploadFiles(e.target.files); e.target.value = ""; }}
          data-testid={`${testIdPrefix}-input`}
        />
        {uploading ? (
          <CircularProgress size={28} sx={{ color: "primary.main" }} />
        ) : (
          <>
            <UploadCloud size={28} className="text-gold" style={{ marginBottom: 8 }} />
            <Typography sx={{ fontWeight: 500, fontSize: 15 }}>Drag photos here, or click to browse</Typography>
            <Typography variant="caption" color="text.secondary">JPEG, PNG, WEBP or GIF · up to 8MB each · {MAX_IMAGES} max</Typography>
          </>
        )}
      </Box>

      {value.length > 0 && (
        <Box sx={{ display: "grid", gridTemplateColumns: "repeat(auto-fill, minmax(110px, 1fr))", gap: 1.5, mt: 2.5 }}>
          {value.map((url, i) => (
            <Box key={url} sx={{ position: "relative", aspectRatio: "1 / 1", borderRadius: 3, overflow: "hidden", group: "photo" }} data-testid={`${testIdPrefix}-thumb-${i}`}>
              <Box component="img" src={url} alt="" sx={{ width: "100%", height: "100%", objectFit: "cover" }} />
              {i === 0 && (
                <Box sx={{ position: "absolute", bottom: 4, left: 4, display: "flex", alignItems: "center", gap: 0.4, bgcolor: "rgba(0,0,0,0.65)", color: "#fff", fontSize: 10, fontWeight: 600, px: 0.8, py: 0.3, borderRadius: 999 }}>
                  <Star size={9} fill="#fff" /> Cover
                </Box>
              )}
              <IconButton
                size="small"
                onClick={() => remove(url)}
                data-testid={`${testIdPrefix}-remove-${i}`}
                sx={{ position: "absolute", top: 4, right: 4, width: 22, height: 22, bgcolor: "rgba(0,0,0,0.55)", color: "#fff", "&:hover": { bgcolor: "rgba(0,0,0,0.75)" } }}
              >
                <X size={12} />
              </IconButton>
              {i !== 0 && (
                <Box
                  component="button"
                  type="button"
                  onClick={() => makeCover(url)}
                  data-testid={`${testIdPrefix}-make-cover-${i}`}
                  sx={{
                    position: "absolute", bottom: 4, left: 4, fontSize: 10, fontWeight: 600, px: 0.8, py: 0.3, borderRadius: 999,
                    border: "none", cursor: "pointer", bgcolor: "rgba(0,0,0,0.55)", color: "#fff", font: "inherit",
                    "&:hover": { bgcolor: "rgba(0,0,0,0.75)" },
                  }}
                >
                  Make cover
                </Box>
              )}
            </Box>
          ))}
        </Box>
      )}
    </Box>
  );
}
